import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ExtractBuildFailedError,
  FULL_SCOPE,
  type BuildingProgress,
  type ExtractPipeline,
  type ExtractPopulation,
  type ExtractScope,
  scopeFingerprint,
} from '../services/reportingApi';
import type {
  ColumnType,
  DatasetManifestEntry,
  ReportDefinition,
} from '../types/reporting';
import { useStore } from '../store';
import type { ResultRow } from './arrowToRows';
import { NO_RELATED, type RelatedManifests, splitRef } from './columnRefs';
import {
  type AxisConstraint,
  boundsOf,
  tighterLower,
  tighterUpper,
} from './filterBounds';
import {
  DETAIL_ROW_LIMIT,
  PIVOT_MAX_VALUES,
  POPULATION_DATASET_KEYS,
  ReportCompileError,
  compileDistinctCount,
  compileReport,
  isFilterActive,
  joinedDatasetKeys,
  normalizePopulationOptions,
  prepareDefinition,
  validateDefinition,
} from './compileReport';
import { loadExcludeInvoiceClaims } from './agencyReportPolicy';
import { backfillJoinedDatasets } from './definitionUpdates';
import { hasCustomEmptyDetailColumns } from './detailColumns';
import {
  type DatasetRequest,
  type DatasetSession,
  DuckDBUnsupportedError,
  withDatasets,
} from './duckdbEngine';

// ---------------------------------------------------------------------------
// Extract scope derivation (SCALING-DESIGN §8, decisions D1 + D4)
// ---------------------------------------------------------------------------

// The date window the primary extract was actually fetched with, surfaced so the
// user can see it (5.2). A restriction the user cannot see is a silent data
// restriction — the design calls that out by name for the default axis.
export interface EffectiveWindow {
  axis: string;
  axisLabel: string;
  lower: string | null;
  upper: string | null;
}

/**
 * The partition-pruning scope for the primary extract, derived from the report's
 * OWN date filters.
 *
 * Deliberately derived, never imposed: the client applies no default window of
 * its own, so a report with no date filter still fetches everything and the
 * user's rows can never go missing because of a window they did not ask for. A
 * derived window is exactly the predicate the browser is about to execute
 * anyway, so pruning to it cannot change the answer.
 *
 * MUST be called on the PREPARED definition — `prepareDefinition` resolves
 * `relative_date` ("last 90 days") into a concrete `between`, and only inactive
 * filters (`isFilterActive`) are skipped by the compiler's WHERE. Deriving from
 * a filter the compiler will NOT apply would fetch a narrower window than the
 * query semantically covers, silently dropping rows.
 *
 * Everything that isn't understood falls through to FULL_SCOPE.
 */
export function deriveExtractScope(
  prepared: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests
): ExtractScope {
  // DERIVED columns (e.g. days_outstanding) are computed in the browser and are
  // absent from the Parquet, so they can never be a server-side pruning axis.
  //
  // `prunable_axes` narrows this further to the axes the server can ACTUALLY
  // prune on (zone-mapped and served). Windowing a non-prunable axis is not
  // wrong — the server fails open and returns the full dataset — but it mints a
  // distinct cache entry per window that all hold the same full bytes. Asking
  // for scope=full instead collapses them into one. `undefined` = an older
  // server that does not advertise, so keep the legacy behaviour.
  const prunable = dataset.prunable_axes;
  const dateColumns = new Set(
    dataset.columns
      .filter(
        c =>
          c.type === 'date' &&
          !c.derived_from &&
          (prunable === undefined || prunable.includes(c.name))
      )
      .map(c => c.name)
  );
  const order: string[] = [];
  const constraints = new Map<string, AxisConstraint>();
  for (const filter of prepared.filters) {
    // Related-dataset filters (dotted refs) are irrelevant here: only the
    // primary is ever windowed (D4).
    const { dataset: refDataset, column } = splitRef(filter.column);
    if (refDataset !== null || !dateColumns.has(column)) continue;
    if (!isFilterActive(filter, dataset, related)) continue;
    const bounds = boundsOf(filter);
    if (!bounds) continue;
    let constraint = constraints.get(column);
    if (!constraint) {
      constraint = {};
      constraints.set(column, constraint);
      order.push(column);
    }
    if (bounds.lower)
      constraint.lower = tighterLower(constraint.lower, bounds.lower);
    if (bounds.upper)
      constraint.upper = tighterUpper(constraint.upper, bounds.upper);
  }
  // The wire contract carries ONE predicate, so pick the first constrained axis
  // in filter order. Pruning on one conjunct of an AND is always sound — the
  // other filters can only narrow further, and the browser applies them all.
  for (const axis of order) {
    const { lower, upper } = constraints.get(axis)!;
    if (!lower && !upper) continue;
    // A contradictory range (lower > upper) matches nothing. Rather than send a
    // shape the server would have to reject, fail open — the browser's WHERE
    // will return the empty result either way.
    if (lower && upper && lower.value > upper.value) return FULL_SCOPE;
    return {
      scope: 'primary',
      axis,
      kind: 'range',
      ...(lower ? { lower: lower.value, lowerInclusive: lower.inclusive } : {}),
      ...(upper ? { upper: upper.value, upperInclusive: upper.inclusive } : {}),
    };
  }
  return FULL_SCOPE;
}

/**
 * D4 — JOIN COMPLETENESS, as an enforced invariant rather than a convention.
 *
 * Each dataset is its own extract and DuckDB joins them in the browser, so a
 * windowed related fetch breaks joins: a visit last month whose episode started
 * 14 months ago fetches the visit but not its episode row, and the LEFT JOIN
 * silently yields NULL episode columns — a wrong answer with no error.
 *
 * The window therefore goes to the PRIMARY dataset only; every related request
 * is pinned to FULL_SCOPE here, in one place, with no parameter that could carry
 * a scope to it.
 */
export function buildDatasetRequests(args: {
  primaryKey: string;
  primaryScope: ExtractScope;
  relatedKeys: readonly string[];
  pipeline?: ExtractPipeline;
  population?: ExtractPopulation;
  force: boolean;
  // Only the primary request reports build progress to the runner. Related
  // extracts can build in parallel without claiming the page-level message.
  onBuilding?: (progress: BuildingProgress) => void;
  // Aborted when the run that owns these requests is torn down. Attached to
  // EVERY request (primary and related) so no leg of a multi-extract load keeps
  // polling after the user leaves the report.
  signal?: AbortSignal;
}): DatasetRequest[] {
  return [
    {
      key: args.primaryKey,
      scope: args.primaryScope,
      ...(args.pipeline ? { pipeline: args.pipeline } : {}),
      ...(args.population ? { population: args.population } : {}),
      ...(args.force ? { force: true } : {}),
      ...(args.onBuilding ? { onBuilding: args.onBuilding } : {}),
      ...(args.signal ? { signal: args.signal } : {}),
    },
    // `force` propagates to related datasets; `scope` deliberately does NOT (D4
    // above). They are different axes: pinning the scope keeps the join complete,
    // whereas withholding the force would let a Refresh rebuild the primary and
    // then join it against a cached related extract — a report showing fresh visits
    // beside stale episode attributes, stamped with only the primary's builtAt. The
    // extra rebuild cost is bounded by the server's refresh cooldown, which answers
    // an already-fresh dataset with a 429 the client falls back to a plain fetch.
    ...args.relatedKeys.map(key => ({
      key,
      scope: FULL_SCOPE,
      ...(args.pipeline ? { pipeline: args.pipeline } : {}),
      ...(args.population ? { population: args.population } : {}),
      ...(args.force ? { force: true } : {}),
      ...(args.signal ? { signal: args.signal } : {}),
    })),
  ];
}

// Display window for the current scope: null when the whole dataset was fetched
// (nothing to disclose — there is no restriction).
function windowOf(
  scope: ExtractScope,
  dataset: DatasetManifestEntry
): EffectiveWindow | null {
  if (scope.scope === 'full') return null;
  const column = dataset.columns.find(c => c.name === scope.axis);
  return {
    axis: scope.axis,
    axisLabel: column?.label ?? scope.axis,
    lower: scope.lower ?? null,
    upper: scope.upper ?? null,
  };
}

// What one pass of the run produced. 'scope-unjustified' is internal: the
// extract-schema gate invalidated the window this pass was fetched with, so the
// caller re-runs at full scope (see the window justification check).
type RunOutcome =
  | { kind: 'pivot-too-wide' }
  | { kind: 'scope-unjustified' }
  | {
      kind: 'ready';
      columns: string[];
      rows: ResultRow[];
      builtAt: string | null;
      dropped: string[];
      testPatientRowsExcluded?: boolean;
      excludedRowCount?: number;
    };

export type RunnerStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'invalid'
  | 'unsupported'
  | 'too-large'
  | 'pivot-too-wide';

export interface RunnerState {
  status: RunnerStatus;
  columns: string[];
  rows: ResultRow[];
  // Client-generated validation message for the 'invalid' status (e.g. "The sum
  // measure needs a group-by or pivot to aggregate over."). Undefined otherwise.
  message?: string;
  // True while re-running with previous rows still on screen (definition edits
  // on an already-loaded dataset). Distinct from the first-load skeleton.
  revalidating: boolean;
  // True from the start of a forced Refresh until it settles, from any prior
  // status. Every terminal state is a new object without it.
  refreshing?: boolean;
  // True after a forced Refresh failed while rows were on screen; they stay.
  // A forced start or any settled run clears it.
  refreshFailed?: boolean;
  // Progress from the primary extract's 202 build poll. Present only during an
  // initial load with no ready result; refreshes and revalidation keep it empty.
  buildingProgress?: BuildingProgress;
  // UTC ISO stamp of the server-side extract build backing the current rows.
  // Null until a run completes; preserved while revalidating.
  builtAt: string | null;
  // Refs dropped at RUN time because the fetched extracts can't serve them
  // (stale extract, mid-session permission change). Undefined/empty when nothing
  // was dropped. Task 12 merges these into the "removed columns" notice.
  droppedColumns?: string[];
  // The date window the primary extract was fetched with (5.2), or null when the
  // whole dataset was fetched. Rendered next to "Data as of" so a windowed fetch
  // is never a silent restriction.
  effectiveWindow: EffectiveWindow | null;
  // True when the diagnostic found report-output rows hidden by the default
  // test-patient exclusion, including when a detail limit makes the exact count
  // unavailable.
  testPatientRowsExcluded?: boolean;
  // Exact positive report-output rows hidden by the default test-patient
  // exclusion. Undefined when the diagnostic cannot calculate an exact count.
  excludedRowCount?: number;
}

export interface Runner extends RunnerState {
  reload: () => void;
  // Force a server-side extract rebuild (?refresh=1) and unconditionally
  // re-register the DuckDB view. Distinct from reload(), which re-runs
  // without forcing (the error-state "Try again" action).
  refreshData: () => void;
}

const isHttp413 = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 413;

// "Data as of" is the OLDEST build across the report's datasets. Compared as
// instants (the server's microsecond precision varies); the original string is
// returned.
export function oldestBuiltAt(
  session: DatasetSession,
  keys: readonly string[]
): string | null {
  let oldest: string | null = null;
  for (const key of keys) {
    const stamp = session.builtAt(key);
    if (
      stamp !== null &&
      (oldest === null || Date.parse(stamp) < Date.parse(oldest))
    ) {
      oldest = stamp;
    }
  }
  return oldest;
}

// Re-validate a joined definition against EXTRACT reality (round 3, typed in
// round 4): the manifest promises what the server would serve NOW, but the
// registered Parquet may be older (S3 freshness window, mid-session deploy),
// thinner (a permission change alters served columns at a constant version — so
// version-int comparison can never be a sufficient gate), or differently TYPED
// (a version bump retyped a column the stale extract still carries in its old
// shape). Refs the extracts can't serve are dropped validate-and-drop style;
// each kept manifest column's type is OVERRIDDEN with the extract's actual type
// so validateDefinition's type-compat pass (Task 9) also drops clauses the
// extract can't execute (date_trunc on a string, contains on a number); a
// related dataset whose join key is missing on either side is dropped wholesale.
// Primary-only definitions never reach here (zero-churn invariant).
async function applyExtractSchemaGate(
  session: DatasetSession,
  prepared: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests,
  relatedKeys: string[]
): Promise<{
  definition: ReportDefinition;
  dropped: string[];
  // The extract-typed manifests the gate validated against. The runner compiles
  // with THESE, not the originals — otherwise compileReport's own
  // assertMeasuresValid re-reads the manifest's promised types and a measure on a
  // column that is numeric-in-manifest but string-in-extract slips through to
  // DuckDB as sum(VARCHAR), the exact binder error this gate exists to prevent.
  primary: DatasetManifestEntry;
  relatedManifests: RelatedManifests;
}> {
  const schemas = new Map<string, Map<string, ColumnType>>();
  await Promise.all(
    [dataset.key, ...relatedKeys].map(async key => {
      schemas.set(key, await session.columns(key));
    })
  );
  // getDatasetColumns probes the fetched Parquet, which omits DERIVED columns
  // (e.g. claims.days_outstanding) — the frontend computes them at serve time
  // from a source column that IS in the Parquet. Union each manifest's DERIVED
  // columns (with their manifest type) into the probed schema so
  // restrictToExtract KEEPS them instead of dropping the ref by name, which
  // would otherwise strip a joined report's days_outstanding group-by.
  const manifestFor = (key: string): DatasetManifestEntry | undefined =>
    key === dataset.key ? dataset : related.get(key);
  for (const [key, schema] of schemas) {
    for (const col of manifestFor(key)?.columns ?? []) {
      if (col.derived_from && !schema.has(col.name)) {
        schema.set(col.name, col.type);
      }
    }
  }
  const restrictToExtract = (
    entry: DatasetManifestEntry,
    schema: Map<string, ColumnType>
  ): DatasetManifestEntry => ({
    ...entry,
    columns: entry.columns.flatMap(c => {
      const extractType = schema.get(c.name);
      // Absent from the extract -> the ref drops by name; present -> judge
      // transforms/filter ops against the extract's ACTUAL type, not the
      // manifest's promise.
      return extractType === undefined ? [] : [{ ...c, type: extractType }];
    }),
  });
  const primarySchema = schemas.get(dataset.key)!;
  const availableRelated = new Map<string, DatasetManifestEntry>();
  for (const key of relatedKeys) {
    const entry = related.get(key);
    const rel = (dataset.relationships ?? []).find(r => r.target === key);
    const schema = schemas.get(key);
    // Join keys must not just EXIST on both sides — they must share a type in
    // the FETCHED extracts. restrictToExtract retypes column REFs from extract
    // reality, but the join keys are gated here, separately, so a drifted
    // extract that retyped one side (e.g. primary episode_id string UUID vs a
    // target episode_id that came back numeric) would otherwise keep the LEFT
    // JOIN and hand DuckDB `VARCHAR = INTEGER` — a runtime conversion error on
    // non-numeric ids, the exact binder error this gate exists to turn into a
    // clean degrade. Mismatched (or missing) key types drop the whole dataset.
    const localKeyType = rel ? primarySchema.get(rel.local_key) : undefined;
    const targetKeyType = rel ? schema?.get(rel.target_key) : undefined;
    if (
      !entry ||
      !rel ||
      !schema ||
      localKeyType === undefined ||
      targetKeyType === undefined ||
      localKeyType !== targetKeyType
    ) {
      continue; // whole dataset unavailable -> its refs drop below
    }
    availableRelated.set(key, restrictToExtract(entry, schema));
  }
  const restrictedPrimary = restrictToExtract(dataset, primarySchema);
  const { definition, dropped } = validateDefinition(
    prepared,
    restrictedPrimary,
    availableRelated
  );
  // Prune joined_datasets to the datasets that survived the gate. compileReport
  // LEFT JOINs every joined_datasets entry (explicit intent), so a joined dataset
  // whose join key drifted — missing or mismatched-type on either side, dropped
  // from availableRelated above — must be removed here or the compiler would join
  // against an extract we couldn't validate (VARCHAR = INTEGER, or an unregistered
  // table). A joined dataset whose join key is intact but whose COLUMN drifted
  // STAYS joined: its column ref was already dropped and recorded in `dropped`,
  // and the unique-key LEFT JOIN itself is harmless (no fan-out) — the join is
  // explicit intent, not a byproduct of the now-dropped column.
  const survivingJoins = (definition.joined_datasets ?? []).filter(key =>
    availableRelated.has(key)
  );
  return {
    definition: { ...definition, joined_datasets: survivingJoins },
    dropped,
    primary: restrictedPrimary,
    relatedManifests: availableRelated,
  };
}

export function useReportRunner(
  definition: ReportDefinition | null,
  dataset: DatasetManifestEntry | null,
  related: RelatedManifests = NO_RELATED,
  options?: {
    debounceMs?: number;
    immediateKey?: number;
    pendingSignatureBillingReadiness?: boolean;
    paymentOasisUpdatedBehavior?: boolean;
  }
): Runner {
  const debounceMs = options?.debounceMs ?? 150;
  const pendingSignatureBillingReadiness =
    options?.pendingSignatureBillingReadiness === true;
  const paymentOasisUpdatedBehavior =
    options?.paymentOasisUpdatedBehavior === true;
  const agencyId = useStore(state => state.user?.agency?.id);
  const [state, setState] = useState<RunnerState>({
    status: 'idle',
    columns: [],
    rows: [],
    revalidating: false,
    builtAt: null,
    effectiveWindow: null,
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const runIdRef = useRef(0);
  const lastDatasetKeyRef = useRef<string | null>(null);
  // Holds the dataset key whose next run should force a rebuild — NOT a bare
  // boolean. A boolean would be consumed by whichever debounce timer fires next,
  // so a refresh queued for one dataset could force a different one after the
  // user switched. Scoping the flag to a key lets only the targeted dataset's run
  // consume it, exactly once.
  const forceKeyRef = useRef<string | null>(null);
  // The definition the rows on screen were built from. A failed Refresh keeps
  // those rows only when it ran against that same definition.
  const readyDefinitionRef = useRef<ReportDefinition | null>(null);
  const lastImmediateKeyRef = useRef<number | undefined>(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const reload = useCallback(() => setReloadNonce(n => n + 1), []);
  const refreshData = useCallback(() => {
    if (dataset) forceKeyRef.current = dataset.key;
    setReloadNonce(n => n + 1);
  }, [dataset]);

  useEffect(() => {
    if (!definition || !dataset) {
      lastDatasetKeyRef.current = null;
      // The refresh target is gone; drop any pending force so it can't leak onto
      // a later run.
      forceKeyRef.current = null;
      // Invalidate any in-flight run so its async completion can't overwrite this
      // idle state with stale rows/builtAt. The timer is cleared on cleanup, but
      // a run already awaiting withDatasets would otherwise still
      // pass the run-ID check and restore a 'ready' snapshot after the dataset
      // became unavailable.
      runIdRef.current += 1;
      setState({
        status: 'idle',
        columns: [],
        rows: [],
        revalidating: false,
        builtAt: null,
        effectiveWindow: null,
      });
      return;
    }
    const runId = ++runIdRef.current;
    const isNewDataset = lastDatasetKeyRef.current !== dataset.key;
    const runImmediately =
      options?.immediateKey !== undefined &&
      options.immediateKey !== lastImmediateKeyRef.current;
    lastImmediateKeyRef.current = options?.immediateKey;
    // A forced refresh never owns the initial-build message, even when it is
    // queued before the first result exists. Loaded-result revalidation is also
    // deliberately quiet; its existing rows remain visible.
    const forceQueued = forceKeyRef.current === dataset.key;
    const canReportBuilding =
      !forceQueued && (isNewDataset || stateRef.current.status !== 'ready');
    lastDatasetKeyRef.current = dataset.key;
    // A force queued for a different dataset (the user switched before the
    // debounce fired) must not leak onto this run — only the dataset it targeted
    // may consume it.
    if (forceKeyRef.current !== null && forceKeyRef.current !== dataset.key) {
      forceKeyRef.current = null;
    }
    // Keep the prior rows visible during revalidation. Initial loads stay
    // neutral until the server sends a real 202 build callback.
    setState(prev =>
      isNewDataset || prev.status !== 'ready'
        ? {
            status: 'loading',
            columns: [],
            rows: [],
            revalidating: false,
            ...(forceQueued ? { refreshing: true } : {}),
            builtAt: null,
            effectiveWindow: null,
          }
        : {
            ...prev,
            // A forced Refresh keeps the notice for the rows still on screen. A
            // plain run never shows Refreshing, even if it supersedes a forced one.
            ...(forceQueued
              ? { refreshing: true, refreshFailed: undefined }
              : {
                  testPatientRowsExcluded: undefined,
                  excludedRowCount: undefined,
                  refreshing: undefined,
                }),
            revalidating: true,
            buildingProgress: undefined,
          }
    );
    const onBuilding = canReportBuilding
      ? (progress: BuildingProgress): void => {
          if (runIdRef.current !== runId) return;
          setState(prev =>
            prev.status === 'loading' && !prev.revalidating
              ? { ...prev, buildingProgress: progress }
              : prev
          );
        }
      : undefined;

    // Torn down with the effect. Without it the runner's runId guard only
    // suppressed the stale setState — the request itself kept going, and a
    // `202 building` poll has no client timeout, so leaving a report mid-build
    // left the tab polling the API until the build finished.
    const abort = new AbortController();
    const timer = setTimeout(
      async () => {
        // Consume the force flag exactly once, and only for the dataset it was
        // queued against: only the run triggered by refreshData() forces a rebuild;
        // a subsequent definition-edit or reload() run must not inherit it.
        const force = forceKeyRef.current === dataset.key;
        if (force) forceKeyRef.current = null;
        try {
          // Sanitize first (pure) so the query always matches what we render and
          // dropped related refs never trigger loads: columns that vanished from
          // the dataset are dropped, and a ref to a dataset missing from `related`
          // (permission lost, edge removed) is dropped by validateDefinition, so
          // relatedKeys below only names loadable datasets. Invalid measures are
          // deliberately left intact rather than silently downgraded to a count —
          // an aggregate with no column (or a non-numeric column) surfaces as a
          // compile error below, so the runner never executes a query that answers
          // a different question than the definition on screen.
          //
          // DELIBERATE ASYMMETRY (round 7): this pre-load pass DISCARDS its
          // `.dropped` list, while the post-load extract-schema gate's drops are
          // surfaced via droppedColumns. Rationale: pre-load drops are
          // manifest-level facts the builder's OPEN path already validated and
          // surfaced with the same notice (Task 12 runs validateDefinition against
          // the live manifests before the runner ever sees the definition) —
          // re-reporting them here would double-count every open-time drop; the
          // runner's channel exists for what only the runner can see, extract
          // reality diverging from the manifest. A future non-builder caller that
          // skips the open path would lose pre-load drop visibility — that is this
          // comment's warning.
          const preparedBase = backfillJoinedDatasets(
            validateDefinition(prepareDefinition(definition), dataset, related)
              .definition,
            related
          );
          const isInitialDetail =
            preparedBase.measures.length === 0 &&
            preparedBase.group_bys.length === 0 &&
            !preparedBase.pivot_column;
          if (isInitialDetail && hasCustomEmptyDetailColumns(preparedBase)) {
            throw new ReportCompileError('Choose at least one column.');
          }
          // The compiler LEFT JOINs every dataset in joined_datasets, whether or
          // not a column references it (explicit intent). backfillJoinedDatasets
          // normalizes that list to the caller's permitted, declared joins —
          // deriving it from dotted refs for a legacy definition, pruning any entry
          // no longer permitted, and unioning in any still-referenced dataset — so
          // relatedKeys names exactly the extracts to load AND exactly what
          // compileReport will join (both go through joinedDatasetKeys). A dataset
          // the caller lost access to is pruned here, degrading the join, instead of
          // 403-ing on its extract load.
          const relatedKeys = [
            ...new Set([
              ...joinedDatasetKeys(preparedBase),
              ...(preparedBase.pipeline?.type === 'patient_roster'
                ? preparedBase.pipeline.support_datasets
                : []),
            ]),
          ];
          const isPatientRoster =
            preparedBase.pipeline?.type === 'patient_roster';
          const agencyExcludesInvoiceClaims =
            await loadExcludeInvoiceClaims(agencyId);
          const population = normalizePopulationOptions(
            preparedBase,
            agencyExcludesInvoiceClaims
          );
          // The window for the PRIMARY extract, derived from this report's own
          // date filters. buildDatasetRequests pins every related extract to
          // FULL_SCOPE (D4 join completeness).
          const requestedScope = isPatientRoster
            ? FULL_SCOPE
            : deriveExtractScope(preparedBase, dataset, related);
          // Load every extract, then register and query them inside ONE session:
          // a dataset's DuckDB view holds a single scope at a time, so the schema
          // probe, the pivot-cardinality probe and the final query must all run
          // with these exact registrations pinned. Each fetch is independently
          // permission-gated server-side; a failure (403 race, network) rejects and
          // lands in the catch below.
          const runOnce = (
            primaryScope: ExtractScope,
            forceThisPass: boolean
          ): Promise<RunOutcome> =>
            withDatasets(
              buildDatasetRequests({
                primaryKey: dataset.key,
                primaryScope,
                relatedKeys,
                pipeline: preparedBase.pipeline?.type,
                population,
                force: forceThisPass,
                onBuilding,
                signal: abort.signal,
              }),
              async session => {
                let prepared = preparedBase;
                let runDropped: string[] = [];
                // Compile against the ORIGINAL manifests for a primary-only run (the
                // gate never runs), and against the EXTRACT-typed manifests for a
                // joined run, so every downstream check (assertMeasuresValid, "empty
                // columns = all columns", type-compat) judges against what the
                // fetched Parquet actually contains instead of the manifest's
                // promise.
                let compileDataset = dataset;
                let compileRelated = related;
                if (relatedKeys.length > 0 && !isPatientRoster) {
                  // Whether the user selected explicit detail columns BEFORE the gate
                  // ran — the signal that distinguishes "user asked for all columns"
                  // (empty from the start) from "every column the user asked for was
                  // dropped".
                  const hadExplicitColumns = prepared.columns.length > 0;
                  const gated = await applyExtractSchemaGate(
                    session,
                    prepared,
                    dataset,
                    related,
                    relatedKeys
                  );
                  prepared = gated.definition;
                  runDropped = gated.dropped;
                  // The gate can remove every aggregate clause. Recompute from
                  // the gated definition so a report that became detail-shaped
                  // cannot fall through to the legacy empty-columns default.
                  const isDetailAfterGate =
                    prepared.measures.length === 0 &&
                    prepared.group_bys.length === 0 &&
                    !prepared.pivot_column;
                  compileDataset = gated.primary;
                  compileRelated = gated.relatedManifests;
                  // WINDOW JUSTIFICATION CHECK. The gate can DROP the very filter the
                  // window was derived from (a column the extract carries with a
                  // different type loses its incompatible date clause). The compiled
                  // query would then range over more than we fetched — silently
                  // missing rows outside the window. Re-derive from the gated
                  // definition against extract-typed manifests; if the window is no
                  // longer exactly justified, abandon this pass and re-run at full
                  // scope rather than answer from a window nothing supports.
                  if (
                    primaryScope.scope === 'primary' &&
                    scopeFingerprint(
                      deriveExtractScope(
                        prepared,
                        compileDataset,
                        compileRelated
                      )
                    ) !== scopeFingerprint(primaryScope)
                  ) {
                    return { kind: 'scope-unjustified' as const };
                  }
                  // A DETAIL report (no measures / group-bys / pivot) whose every
                  // selected column was dropped by the gate must NOT fall through to
                  // compileReport's "empty columns = all columns" default — the user
                  // asked for specific columns and would otherwise silently get every
                  // primary column instead. Surface it as an actionable invalid state
                  // (the removed columns are already recorded in runDropped).
                  if (
                    isDetailAfterGate &&
                    hadExplicitColumns &&
                    prepared.columns.length === 0
                  ) {
                    throw new ReportCompileError(
                      'None of the selected columns are available in the current data.'
                    );
                  }
                }
                const isDetail =
                  prepared.measures.length === 0 &&
                  prepared.group_bys.length === 0 &&
                  !prepared.pivot_column;
                if (prepared.pivot_column && prepared.measures.length > 0) {
                  const probe = await session.query(
                    compileDistinctCount(
                      prepared,
                      compileDataset,
                      compileRelated,
                      { agencyExcludesInvoiceClaims }
                    )
                  );
                  const cardinality = Number(probe.rows[0]?.n ?? 0);
                  if (cardinality > PIVOT_MAX_VALUES) {
                    return { kind: 'pivot-too-wide' as const };
                  }
                }
                const compileOptions = {
                  pendingSignatureBillingReadiness,
                  paymentOasisUpdatedBehavior,
                  agencyExcludesInvoiceClaims,
                };
                const sql = compileReport(
                  prepared,
                  compileDataset,
                  compileRelated,
                  compileOptions
                );
                const result = await session.query(sql);
                // EMR-846: disclose rows the default test-patient exclusion
                // hid. Key off population membership, not the manifest, which
                // hides is_test_patient. Re-query only while the population
                // is excluding test rows.
                const excludesTestPatients =
                  POPULATION_DATASET_KEYS.has(prepared.dataset_key) &&
                  !population.includeTestRows;
                let testPatientRowsExcluded: boolean | undefined;
                let excludedRowCount: number | undefined;
                if (excludesTestPatients && isDetail) {
                  // Flip only include_test_rows so the delta is rows hidden
                  // by the test-patient predicate alone. Grouped and pivot
                  // reports do not disclose detail-row exclusions by design.
                  const unfilteredDefinition = {
                    ...prepared,
                    include_test_rows: true,
                  };
                  try {
                    if (result.rows.length >= DETAIL_ROW_LIMIT) {
                      // The detail query is capped, so equal lengths prove
                      // nothing. Detect with scalar counts and keep the
                      // disclosure non-numeric: shown + excluded cannot equal
                      // the unfiltered total.
                      const countDefinition = {
                        ...prepared,
                        columns: [],
                        group_bys: [],
                        pivot_column: null,
                        measures: [{ agg: 'count' as const }],
                        sort: [],
                      };
                      const filteredCountResult = await session.query(
                        compileReport(
                          countDefinition,
                          compileDataset,
                          compileRelated,
                          compileOptions
                        )
                      );
                      const unfilteredCountResult = await session.query(
                        compileReport(
                          { ...countDefinition, include_test_rows: true },
                          compileDataset,
                          compileRelated,
                          compileOptions
                        )
                      );
                      const filteredCount = Number(
                        filteredCountResult.rows[0]?.count ?? 0
                      );
                      const unfilteredCount = Number(
                        unfilteredCountResult.rows[0]?.count ?? 0
                      );
                      if (unfilteredCount > filteredCount) {
                        testPatientRowsExcluded = true;
                      }
                    } else {
                      const unfilteredResult = await session.query(
                        compileReport(
                          unfilteredDefinition,
                          compileDataset,
                          compileRelated,
                          compileOptions
                        )
                      );
                      const difference =
                        unfilteredResult.rows.length - result.rows.length;
                      if (difference > 0) {
                        testPatientRowsExcluded = true;
                        if (unfilteredResult.rows.length < DETAIL_ROW_LIMIT) {
                          excludedRowCount = difference;
                        }
                      }
                    }
                  } catch (error) {
                    // The comparison is diagnostic only. Never discard a valid
                    // filtered report when its unfiltered comparison fails.
                    console.error(
                      'Test-patient exclusion diagnostic failed',
                      error
                    );
                  }
                }
                return {
                  kind: 'ready' as const,
                  columns: result.columns,
                  rows: result.rows,
                  builtAt: oldestBuiltAt(session, [
                    dataset.key,
                    ...relatedKeys,
                  ]),
                  dropped: runDropped,
                  testPatientRowsExcluded,
                  excludedRowCount,
                };
              }
            );
          // A second pass can only happen when the first ran WINDOWED and the gate
          // invalidated that window; FULL_SCOPE is always justified, so this
          // terminates after at most one retry.
          let primaryScope = requestedScope;
          let outcome = await runOnce(primaryScope, force);
          if (outcome.kind === 'scope-unjustified') {
            primaryScope = FULL_SCOPE;
            // The first pass already forced the rebuild; re-forcing here would ask
            // for a SECOND rebuild off one Refresh click — and at FULL_SCOPE that
            // means all of history rather than the candidate window. The retry only
            // needs the full-scope BYTES, which a plain conditional GET returns.
            outcome = await runOnce(primaryScope, false);
          }
          const effectiveWindow = windowOf(primaryScope, dataset);
          if (runIdRef.current !== runId) return;
          if (outcome.kind !== 'ready') {
            // 'pivot-too-wide' is the only non-ready outcome that can reach here: a
            // full-scope pass never reports 'scope-unjustified', so the retry above
            // always resolves that case.
            setState({
              status: 'pivot-too-wide',
              columns: [],
              rows: [],
              revalidating: false,
              builtAt: null,
              effectiveWindow: null,
            });
            return;
          }
          readyDefinitionRef.current = definition;
          setState({
            status: 'ready',
            columns: outcome.columns,
            rows: outcome.rows,
            revalidating: false,
            builtAt: outcome.builtAt,
            droppedColumns: outcome.dropped,
            effectiveWindow,
            testPatientRowsExcluded: outcome.testPatientRowsExcluded,
            excludedRowCount: outcome.excludedRowCount,
          });
        } catch (error) {
          if (runIdRef.current !== runId) return;
          // The definition is sanitized above, so a compile error is no longer an
          // expected mid-edit state. Surface it as an error instead of silently
          // holding stale (still-exportable) results or a misleading idle prompt.
          console.error('Report query failed', error);
          if (error instanceof ReportCompileError) {
            // Client-side validation the user can act on (e.g. a Sum with no
            // group-by/pivot) — surface its actionable message directly, like the
            // too-large / pivot-too-wide notices, with no "Try again" retry.
            setState({
              status: 'invalid',
              columns: [],
              rows: [],
              message: error.message,
              revalidating: false,
              builtAt: null,
              effectiveWindow: null,
            });
          } else if (error instanceof DuckDBUnsupportedError) {
            setState({
              status: 'unsupported',
              columns: [],
              rows: [],
              revalidating: false,
              builtAt: null,
              effectiveWindow: null,
            });
          } else if (isHttp413(error)) {
            setState({
              status: 'too-large',
              columns: [],
              rows: [],
              revalidating: false,
              builtAt: null,
              effectiveWindow: null,
            });
          } else if (error instanceof ExtractBuildFailedError) {
            // HTTP 503: the build failed terminally. Show the retryable `error`
            // state (reload() resets the server fail budget), except that a failed
            // forced Refresh keeps the rows on screen if their definition did not
            // change while it was queued.
            const keepRows = force && readyDefinitionRef.current === definition;
            setState(prev =>
              keepRows && prev.status === 'ready'
                ? {
                    ...prev,
                    revalidating: false,
                    refreshing: undefined,
                    refreshFailed: true,
                    buildingProgress: undefined,
                  }
                : {
                    status: 'error',
                    columns: [],
                    rows: [],
                    revalidating: false,
                    builtAt: null,
                    effectiveWindow: null,
                  }
            );
          } else {
            setState({
              status: 'error',
              columns: [],
              rows: [],
              revalidating: false,
              builtAt: null,
              effectiveWindow: null,
            });
          }
        }
      },
      runImmediately ? 0 : debounceMs
    );
    return () => {
      clearTimeout(timer);
      // Invalidate this run BEFORE aborting, so the cancellation rejection lands
      // on the catch's stale-run guard and is discarded rather than rendering an
      // error state for a report the user has already navigated away from.
      runIdRef.current += 1;
      abort.abort();
    };
    // `related` must be a memoized map (Task 12 provides one) so this effect
    // doesn't re-run every render.
  }, [
    definition,
    dataset,
    related,
    debounceMs,
    options?.immediateKey,
    pendingSignatureBillingReadiness,
    paymentOasisUpdatedBehavior,
    agencyId,
    reloadNonce,
  ]);

  return { ...state, reload, refreshData };
}
